import { getCompany } from "../shared/data/storage/authStorage";

/**
 * Get effective date based on whether account is demo or not
 * Demo accounts use March 31, 2025 (so current month is March 2025)
 * Real accounts use current date
 *
 * @returns Date object representing the effective current date
 */
export function getEffectiveDate(): Date {
  const company = getCompany();
  const isDemo =
    company?.companyName?.toLowerCase() === "demo company" ||
    company?.companyName?.toLowerCase() === "demo-ip-client";

  if (isDemo) {
    // Demo account: March 31, 2025 (so current month is March 2025)
    return new Date(2025, 2, 31); // Month is 0-indexed, so 2 = March
  }

  // Real account: current date
  return new Date();
}

/**
 * Get effective current year
 *
 * @returns Current year based on effective date
 */
export function getEffectiveYear(): number {
  return getEffectiveDate().getFullYear();
}

/**
 * Get effective current month (1-12)
 *
 * @returns Current month (1-12) based on effective date
 */
export function getEffectiveMonth(): number {
  return getEffectiveDate().getMonth() + 1; // getMonth() returns 0-11, so +1 for 1-12
}

function ordinalDay(day: number): string {
  if (day % 10 === 1 && day % 100 !== 11) return `${day}st`;
  if (day % 10 === 2 && day % 100 !== 12) return `${day}nd`;
  if (day % 10 === 3 && day % 100 !== 13) return `${day}rd`;
  return `${day}th`;
}

/**
 * Calendar date string for UI (e.g. "31st March 2025") from {@link getEffectiveDate}.
 */
export function formatEffectiveDateLongOrdinal(): string {
  const d = getEffectiveDate();
  const day = d.getDate();
  const monthName = d.toLocaleDateString("en-GB", { month: "long" });
  const y = d.getFullYear();
  return `${ordinalDay(day)} ${monthName} ${y}`;
}
